export type FormMode = 'create' | 'edit'

export interface ServiceSuggestions {
  imageRepo: string
  domain: string
}

export interface AssistedFieldState {
  value: string
  touched: boolean
}

export interface ServiceSuggestionDefaults {
  imageRepoBase: string
  domainSuffix: string
}

export interface OnboardingServiceLike {
  image_repo?: string | null
}

export interface SingleRepoWorkflowInput {
  imageRepo: string
  webhookURL: string
}

export const serviceNameRE = /^[a-z0-9][a-z0-9-]{1,62}$/

export const defaultServiceSuggestionDefaults: ServiceSuggestionDefaults = {
  imageRepoBase: 'ghcr.io/237676098',
  domainSuffix: 'app.code2.top',
}

export function buildServiceSuggestions(
  name: string,
  defaults: ServiceSuggestionDefaults = defaultServiceSuggestionDefaults,
): ServiceSuggestions | null {
  const trimmed = name.trim()
  if (!serviceNameRE.test(trimmed) || trimmed.endsWith('-')) {
    return null
  }

  return {
    imageRepo: `${defaults.imageRepoBase}/${trimmed}`,
    domain: `${trimmed}.${defaults.domainSuffix}`,
  }
}

export function syncAssistedField(
  mode: FormMode,
  state: AssistedFieldState,
  suggestion: string | null,
): AssistedFieldState {
  if (mode === 'edit' || state.touched || !suggestion) {
    return state
  }

  return {
    value: suggestion,
    touched: false,
  }
}

export function resolveOnboardingImageRepo(
  createdService: OnboardingServiceLike | null,
  formImageRepo: string,
): string {
  return createdService?.image_repo ?? formImageRepo
}

export function openHrefInNewTab(
  href: string,
  open: (url?: string | URL, target?: string, features?: string) => Window | null = window.open,
): void {
  open(href, '_blank', 'noopener')
}

function buildWorkflowPayload(imageRepo: string): string {
  return `          {
            "image_repo": "${imageRepo}",
            "tag":        "sha-$COMMIT_SHA",
            "digest":     "$IMAGE_DIGEST",
            "commit_sha": "$COMMIT_SHA",
            "commit_msg": $COMMIT_MSG_JSON,
            "built_at":   "$(date -u +%Y-%m-%dT%H:%M:%SZ)"
          }`
}

export function buildSingleRepoWorkflow({ imageRepo, webhookURL }: SingleRepoWorkflowInput): string {
  const payload = buildWorkflowPayload(imageRepo)

  return `name: Build and notify Services Manager

on:
  push:
    branches:
      - main
      - master
jobs:
  build:
    runs-on: ubuntu-latest
    permissions:
      contents: read
      packages: write
    steps:
      - uses: actions/checkout@v4
      - uses: docker/login-action@v3
        with:
          registry: ghcr.io
          username: \${{ github.actor }}
          password: \${{ secrets.GITHUB_TOKEN }}
      - uses: docker/setup-buildx-action@v3
      - id: build
        uses: docker/build-push-action@v5
        with:
          context: .
          file: Dockerfile
          push: true
          tags: ${imageRepo}:sha-\${{ github.sha }}
          cache-from: type=gha
          cache-to: type=gha,mode=max
      - name: Notify Services Manager
        env:
          SM_WEBHOOK_URL: ${webhookURL}
          SM_WEBHOOK_TOKEN: \${{ secrets.SM_WEBHOOK_TOKEN }}
          IMAGE_DIGEST: \${{ steps.build.outputs.digest }}
          COMMIT_SHA: \${{ github.sha }}
          COMMIT_MSG: \${{ github.event.head_commit.message }}
        run: |
          COMMIT_MSG_JSON=$(printf '%s' "$COMMIT_MSG" | jq -Rs .)
          curl -fsS -X POST "$SM_WEBHOOK_URL" \\
            -H "Authorization: Bearer $SM_WEBHOOK_TOKEN" \\
            -H "Content-Type: application/json" \\
            -d @- <<EOF
${payload}
          EOF
`
}
